import React, { useState, useEffect, useRef } from 'react'
import pjson from "../package.json"
import { ContactForm } from './pages/ContactForm';

interface HeaderProps {}

export function Header(props: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showVersion, setShowVersion] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const versionRef = useRef<HTMLLIElement>(null);

  const isActive = (path: string): boolean => {
    return window.location.pathname === path || window.location.pathname === path + '/';
  };

  // Close version modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (versionRef.current && !versionRef.current.contains(event.target as Node)) {
        setShowVersion(false);
      }
    };

    if (showVersion) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVersion]);

  // Close mobile menu when clicking a link
  const handleNavClick = () => {
    setMenuOpen(false);
  };

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen]);

  let target = "_self";
  if (window.location.href.includes("practice")) target = "_blank";

  return (
    <>
      <header className="Header">
        <a href="/" className="logo-link">
          <img className="logoHeader" src="./logoBlue.svg" alt="UV Game logo" />
        </a>

        {/* Hamburger button - mobile only */}
        <button
          className={`hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Navigation menu */}
        <nav className={`nav-menu ${menuOpen ? 'open' : ''}`}>
          <ul className="nav-primary">
            <li>
              <a
                href="/intro"
                target={target}
                className={isActive('/intro') ? 'active' : ''}
                onClick={handleNavClick}
              >
                Intro
              </a>
            </li>
            <li>
              <a
                href="/basics"
                target={target}
                className={isActive('/basics') ? 'active' : ''}
                onClick={handleNavClick}
              >
                Basics
              </a>
            </li>
            <li>
              <a
                href="/practice"
                target={target}
                className={isActive('/practice') ? 'active' : ''}
                onClick={handleNavClick}
              >
                Practice
              </a>
            </li>
          </ul>

          <ul className="nav-secondary">
            <li>
              <button onClick={() => { setShowContact(!showContact); handleNavClick(); }}>
                Contact
              </button>
            </li>
            <li>
              <a href="https://alexandre-alves.fr" target="_blank" rel="noopener noreferrer" onClick={handleNavClick}>
                Author
              </a>
            </li>
            <li>
              <a href="https://ko-fi.com/alexandrealvesdb" target="_blank" rel="noopener noreferrer" onClick={handleNavClick}>
                Ko-Fi
              </a>
            </li>
            <li className="version-wrapper" ref={versionRef}>
              <button
                className="version-badge"
                onClick={() => setShowVersion(!showVersion)}
                aria-expanded={showVersion}
              >
                v{pjson.version}
              </button>
            </li>
          </ul>
        </nav>

        {/* Backdrop for mobile menu */}
        <div
          className={`nav-backdrop ${menuOpen ? 'visible' : ''}`}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      </header>

      {/* Version Modal */}
      <div className={`version-modal ${showVersion ? 'visible' : ''}`}>
        <div className="version-modal-header">
          <h3>What's new?</h3>
          <button
            className="version-close"
            onClick={() => setShowVersion(false)}
            aria-label="Close version info"
          >
            ×
          </button>
        </div>
        <div className="version-content">
          <div className="version-item">
            <span className="version-number">1.4.0</span>
            <span className="version-desc">Mobile UI improvements, tutorial navigation (TOC, progress bar), and expanded educational content</span>
          </div>
          <div className="version-item">
            <span className="version-number">1.3.1</span>
            <span className="version-desc">Updated broken dependencies</span>
          </div>
          <div className="version-item">
            <span className="version-number">1.3.0</span>
            <span className="version-desc">Change of design for the pages and added buttons to reset view and object</span>
          </div>
          <div className="version-item">
            <span className="version-number">1.2.1</span>
            <span className="version-desc">Added thickness to lines in the practice part</span>
          </div>
          <div className="version-item">
            <span className="version-number">1.2.0</span>
            <span className="version-desc">You can now select edges from the UV window and there is an homepage!</span>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div className="contact-modal-overlay" onClick={() => setShowContact(false)}>
          <div
            className="contact-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
          >
            <button
              className="contact-modal-close"
              onClick={() => setShowContact(false)}
              aria-label="Close contact form"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <ContactForm onClose={() => setShowContact(false)} isOpen={showContact} />
          </div>
        </div>
      )}
    </>
  );
}

// Keep class export for backwards compatibility
export class HeaderClass extends React.Component {
  render() {
    return <Header />;
  }
}
